import { IPromptService } from './IPromptService';
import { Prompt, PromptRequest, PromptResponse } from '../models/PromptTypes';
import EnvConfigHelper from '../../../helpers/envConfigHelper';

const delay = <R>(response: R) => new Promise<R>((resolve) => setTimeout(() => resolve(response), 300));

const now = () => new Date().toISOString();

// Mock prompt data
const mockPrompts: Prompt[] = [
    {
        key: 'mock-1',
        created: now(),
        updated: now(),
        created_by: 'mockuser@example.com',
        created_by_name: 'Mock User',
        updated_by: 'mockuser@example.com',
        title: 'Mock Prompt 1',
        description: 'Description for mock prompt 1',
        content: 'This is the content of mock prompt 1.',
        tags: 'tag1, tag2',
        is_public: true,
        category: 'General',
        assistant_key: 'basic-gpt',
        usage_count: 0,
        last_used: ''
    },
    {
        key: 'mock-2',
        created: now(),
        updated: now(),
        created_by: 'mockuser@example.com',
        created_by_name: 'Mock User',
        updated_by: 'mockuser@example.com',
        title: 'Mock Prompt 2',
        description: 'Description for mock prompt 2',
        content: 'This is the content of mock prompt 2.',
        tags: 'tag3, tag4',
        is_public: false,
        category: 'Advanced',
        assistant_key: 'advanced',
        usage_count: 0,
        last_used: ''
    }
];

class MockPromptService implements IPromptService {
    baseURL = EnvConfigHelper.get('api-base-url', '');

    constructor(public getAccessToken: () => Promise<string | undefined>) {}

    async getPromptsByKey(key: string): Promise<Prompt[]> {
        await this.getAccessToken();
        return delay(mockPrompts.filter(p => p.assistant_key === key));
    }

    async getAllPrompts(): Promise<Prompt[]> {
        await this.getAccessToken();
        return delay([...mockPrompts]);
    }

    async createPrompt(key: string, prompt: PromptRequest): Promise<Prompt> {
        const newPrompt: Prompt = {
            ...prompt,
            key: `mock-${Date.now()}`,
            created: now(),
            updated: now(),
            created_by: 'mock-creator@example.com',
            created_by_name: 'Mock Creator',
            updated_by: 'mock-creator@example.com',
            assistant_key: key,
            usage_count: 0,
            last_used: ''
        };
        mockPrompts.push(newPrompt);
        return delay(newPrompt);
    }

    async getPrompt(key: string, promptKey: string): Promise<Prompt> {
        const prompt = mockPrompts.find(p => p.key === promptKey && p.assistant_key === key);
        if (!prompt) throw new Error('Prompt not found');
        return delay(prompt);
    }

    async updatePrompt(key: string, promptKey: string, promptReq: PromptRequest): Promise<PromptResponse<{}>> {
        const prompt = mockPrompts.find(p => p.key === promptKey && p.assistant_key === key);
        if (!prompt) return delay({ success: false, message: 'Prompt not found', data: {} });

        Object.assign(prompt, promptReq, {
            updated: now(),
            updated_by: 'mock-updater@example.com'
        });

        return delay({ success: true, message: 'Prompt updated', data: {} });
    }

    async deletePrompt(key: string, promptKey: string): Promise<boolean> {
        const index = mockPrompts.findIndex(p => p.key === promptKey && p.assistant_key === key);
        if (index >= 0) {
            mockPrompts.splice(index, 1);
            return delay(true);
        }
        return delay(false);
    }

    async copyPrompt(key: string, promptKey: string, promptReq: PromptRequest): Promise<PromptResponse<Prompt>> {
        const original = mockPrompts.find(p => p.key === promptKey && p.assistant_key === key);
        if (!original) {
            return delay({
                success: false,
                message: 'Original prompt not found',
                data: {} as Prompt
            });
        }

        const newPrompt: Prompt = {
            ...original,
            ...promptReq,
            key: `${promptKey}-copy-${Date.now()}`,
            created: now(),
            updated: now(),
            created_by: 'mock-copier@example.com',
            created_by_name: 'Mock Copier',
            updated_by: 'mock-copier@example.com',
            assistant_key: key
        };

        mockPrompts.push(newPrompt);

        return delay({
            success: true,
            message: 'Prompt copied successfully',
            data: newPrompt
        });
    }
}

export default MockPromptService;